import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@starling/core/api/client';
import type { components } from '@/api/types.vision.gen';

// Раздел «Объявления» (V1.5) — ленты Starling Vision внутри Light-админки.
// Чистая UI-композиция поверх публичного vision-API (`/api/v1/vision/*`,
// контракт docs/api/openapi-vision.yaml): чтение лент — без auth, публикация —
// с текущим Light-токеном (роутер vision принимает те же TokenClaims).
// Паттерн хуков — как у frontend/vision/src/feed/hooks.ts (keyset-курсор VD3).

export type Listing = components['schemas']['Listing'];
export type ListingPage = components['schemas']['ListingPage'];
export type ListingFeed = components['schemas']['ListingFeed'];
export type ListingCreate = components['schemas']['ListingCreate'];
export type ListingCategory = components['schemas']['ListingCategory'];
export type ListingCategoriesResponse = components['schemas']['ListingCategoriesResponse'];
export type MyOrganization = components['schemas']['MyOrganization'];
export type MyOrganizationsResponse = components['schemas']['MyOrganizationsResponse'];

/** Префикс кэша лент — совпадает по смыслу с Vision-фронтом. */
const LISTINGS_KEY_PREFIX = ['vision', 'listings'] as const;

export interface ListingsQueryParams {
  /** Лента обязательна (VD4 контракта). */
  feed: ListingFeed;
  /** Slug категории из GET /vision/categories. */
  category?: string;
  /** Текстовый поиск по title/description. */
  q?: string;
  limit?: number;
}

export function buildListingsUrl(params: ListingsQueryParams, cursor?: string): string {
  const search = new URLSearchParams({ feed: params.feed });
  if (params.category) search.set('category', params.category);
  if (params.q) search.set('q', params.q);
  if (params.limit) search.set('limit', String(params.limit));
  if (cursor) search.set('cursor', cursor);
  return `/api/v1/vision/listings?${search.toString()}`;
}

/**
 * Лента с keyset-пагинацией (VD3): «Показать ещё» докручивает страницы —
 * `next_cursor` предыдущей уходит в `cursor` следующей; null = конец.
 */
export function useListingsQuery(params: ListingsQueryParams) {
  return useInfiniteQuery({
    queryKey: [...LISTINGS_KEY_PREFIX, params],
    queryFn: ({ pageParam }) => api.get<ListingPage>(buildListingsUrl(params, pageParam)),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: ListingPage) => lastPage.next_cursor ?? undefined,
  });
}

/** Справочник категорий: меняется миграциями, поэтому кэш длинный. */
export function useCategoriesQuery() {
  return useQuery({
    queryKey: ['vision', 'categories'],
    queryFn: () => api.get<ListingCategoriesResponse>('/api/v1/vision/categories'),
    staleTime: 60 * 60 * 1000,
  });
}

/** Название категории на активном языке; неизвестный slug → сам slug. */
export function categoryLabel(
  categories: ListingCategory[] | undefined,
  categoryId: string,
  language: string,
): string {
  const found = categories?.find((c) => c.id === categoryId);
  if (!found) return categoryId;
  return language.startsWith('kk') ? found.name_kk : found.name_ru;
}

/**
 * Организации текущего аккаунта (`GET /vision/my/organization`) — питают гейт
 * публикации. У demo-оптик сид даёт verified-организацию; если её нет —
 * форма публикации закрыта подводкой (подтверждение — в кабинете Vision).
 */
export function useMyOrganizationsQuery(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['vision', 'my-organizations'],
    queryFn: () => api.get<MyOrganizationsResponse>('/api/v1/vision/my/organization'),
    staleTime: 5 * 60 * 1000,
    enabled: options?.enabled ?? true,
  });
}

/** Гейт публикации (ТЗ Vision §2.4): организация active + верифицирована. */
export function eligibleOrganizations(orgs: MyOrganization[] | undefined): MyOrganization[] {
  return (orgs ?? []).filter((o) => o.status === 'active' && o.verified_at !== null);
}

/** Контакты организации (№16-C): без ПОДТВЕРЖДЁННОГО телефона/Telegram публикация закрыта. */
export function hasContacts(org: MyOrganization): boolean {
  return org.phone_verified_at !== null || org.telegram_verified_at !== null;
}

/**
 * Публикация (`POST /vision/listings`) с текущим Light-токеном. После успеха
 * инвалидируем ветку лент — свежее объявление сразу видно в выдаче раздела.
 */
export function useCreateListingMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['vision', 'listings', 'create'],
    mutationFn: (input: ListingCreate) => api.post<Listing>('/api/v1/vision/listings', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: LISTINGS_KEY_PREFIX });
    },
  });
}
